import { blobToDataUrl } from './index'

/**
 * base64 페이로드 + MIME 으로부터 File 객체 생성. atob 으로 binary 복원 후 Uint8Array → File.
 *
 * 본 함수는 `dataUrlToBlobFile` 내부 호출 외 직접 사용처도 있어 별도 export 한다.
 */
export function base64ToBlobFile(base64Str: string, mime: string): File {
  const bstr = atob(base64Str)
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], 'blob', { type: mime })
}

/**
 * `data:<mime>;base64,<payload>` 형식의 data URL 을 File 객체로 변환.
 */
export function dataUrlToBlobFile(dataUrl: string): File {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)![1]

  return base64ToBlobFile(arr[1], mime)
}

export interface DataUrlCidResolver {
  resolveCidForDataUrl: (src: string) => { cid: string; file: File } | null | undefined
  registerDataUrlCid: (src: string, cid: string, file: File) => void
}

export interface DataUrlCidOptions {
  host: string
  createUuid: () => string
}

/**
 * NgwEditor 내장 picker / paste / drag 경로로 PM 에 직접 주입된 dataUrl 이미지를 cid 로 변환.
 *
 * 어댑터 urlMap / srcImageMap 어디에도 등록되지 않은 상태로 들어오므로 onPreProcess 핸들러가
 * dataUrl → blob → cid 변환 + inline attachment 등록을 책임진다. 본 함수는 이 중 cid 정합성
 * 보강 부분 (2026-05-19) 을 담당한다:
 *
 * - A) cid 는 UUID 로 생성 — 이전 `uniqueBlobId('inline-')` 패턴은 EmailForm 인스턴스의
 *   counter 가 0 부터 시작이라 답장 시 quoted body 의 동일 시스템 cid (`inline-N@host`) 와
 *   충돌 위험이 있었다. UUID 는 globally unique.
 * - B) dataUrl 별 cid 캐싱 — 매 송신/저장마다 새 cid 생성 + 새 attachment 객체 + 서버 재업로드
 *   가 발생하던 비효율을 해소. 같은 dataUrl 이미지는 lifetime 동안 같은 cid + 같은 File 재사용.
 */
export interface PasteImageContext {
  attachmentLookup: (cid: string) => CidImageAttachment | undefined | null
}

/**
 * paste 된 HTML 안의 `cid:` / `blob:` img 를 data URL 로 in-place 변환.
 *
 * `@ngweditor/react@0.2.10` 의 image NodeSpec parseDOM regex (`^(?:https?:|data:image\/)`) 는
 * cid:/blob: src 를 schema 단계에서 drop 한다. paste 직후 PM 에 넣기 전 본 함수로 변환해야
 * 이미지가 화면에 표시된다.
 *
 * 우선순위:
 *   1. `cid:` 는 `attachmentLookup` 으로 inlineAttachmentMap 에서 직접 변환 (same-origin
 *      fetch 회피 — quoted body 의 cid 이미지를 복사 / paste 했을 때 fetch 가 실패해 화면에
 *      아무 것도 안 표시되던 회귀 fix)
 *   2. `cid:` / `blob:` 모두 `fetch` 로 변환 시도 (same-origin 한정)
 *   3. clipboardData.items 의 image MIME blob fallback (브라우저가 raw 이미지를 동시에 첨부)
 *   4. 모든 시도 실패 시 src 제거 + alt 표시
 */
export async function convertPastedImageSources(
  imgs: HTMLImageElement[],
  imageItems: DataTransferItem[],
  ctx: PasteImageContext,
): Promise<void> {
  const fallbackQueue = [...imageItems]

  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') ?? ''
      if (!src.startsWith('cid:') && !src.startsWith('blob:')) return

      if (src.startsWith('cid:')) {
        const cid = src.substring(4)
        const attachment = ctx.attachmentLookup(cid)
        if (attachment != null) {
          try {
            const downloadObj = await attachment.download()
            const blob = downloadObj?.blob
            if (blob != null) {
              const dataUrl = await blobToDataUrl(blob)
              img.setAttribute('src', dataUrl)
              return
            }
          } catch {
            // attachment 변환 실패 — fetch fallback 진행
          }
        }
      }

      try {
        const response = await fetch(src)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const blob = await response.blob()
        const dataUrl = await blobToDataUrl(blob)
        img.setAttribute('src', dataUrl)
        return
      } catch {
        const fallback = fallbackQueue.length > 0 ? fallbackQueue.shift()?.getAsFile() : null
        if (fallback != null) {
          try {
            const dataUrl = await blobToDataUrl(fallback)
            img.setAttribute('src', dataUrl)
            return
          } catch {
            // fallback 변환도 실패 — alt 표시로 fall-through
          }
        }
        img.removeAttribute('src')
        img.setAttribute('alt', 'inline image (paste failed)')
      }
    }),
  )
}

export function resolveOrCreateDataUrlCid(
  dataUrl: string,
  resolver: DataUrlCidResolver,
  options: DataUrlCidOptions,
): { cid: string; attachmentFile: File } {
  const cached = resolver.resolveCidForDataUrl(dataUrl)
  if (cached != null) {
    return { cid: cached.cid, attachmentFile: cached.file }
  }
  const blob = dataUrlToBlobFile(dataUrl)
  const ext = (blob.type.split('/')[1] ?? 'png').replace(/[^a-z0-9]/gi, '')
  const cid = `${options.createUuid()}@${options.host}`
  const attachmentFile = new File([blob], `inline-${cid}.${ext}`, {
    lastModified: Date.now(),
    type: blob.type,
  })
  resolver.registerDataUrlCid(dataUrl, cid, attachmentFile)
  return { cid, attachmentFile }
}

/**
 * `convertCidImagesToDataUrl` 가 필요로 하는 attachment 최소 인터페이스.
 *
 * 실제 호출처 (EmailForm / SignatureEdit) 의 `inlineAttachmentMap` 은 광범위한 `any` 타입이지만,
 * 본 함수는 `download(): Promise<{ blob }>` 만 의존한다. 의존성을 좁혀 단위 테스트에서 mock 이
 * 단순해지고, helper 시그니처가 호출처의 풀 타입에 종속되지 않도록 한다.
 */
export interface CidImageAttachment {
  download: () => Promise<{ blob?: Blob | null } | null | undefined>
}

/**
 * NgwEditor schema 의 image NodeSpec parseDOM regex (`^(?:https?:|data:image\/)`) 는
 * `cid:` src 를 schema 단계에서 drop 한다. setData / quotedBody.set 직전에 본 함수를 호출해
 * cid 이미지를 data URL 로 사전 변환하면서 어댑터의 urlMap 에 `dataUrl → origCidSrc` 매핑을
 * 등록한다. 송신 시점에는 `IEditorContent.getDataPreprocessed()` 가 본 mapping 으로 역치환
 * 후 `onPreProcess` 핸들러에 임시 DOM 을 전달하므로 기존 cid 매칭 로직이 무수정 재사용된다.
 *
 * binary 회수는 `attachment.download()` 의 idempotent 캐시에 의존 — 답장/resend 경로는 이미
 * `Promise.all` 완료 후라 동기적으로 hit, draft 경로는 본 함수 안에서 실제 download 발생.
 */
/**
 * 이미지의 cid 소스를 해석한다. `src="cid:..."` 가 1순위이고, 없으면 EmailView(parserHTML)가
 * cid 를 백업해 둔 `owa-cid="cid:..."`(이때 src 는 `""`) 또는 legacy `data-cid` 를 사용한다.
 *
 * EmailView 가 표시용으로 `owa-cid` 에 cid 를 옮기고 `src` 를 비우므로, 이 형식이 편집 진입 본문에
 * 유입되면 src 만 보던 기존 로직은 복원에 실패해 이미지가 소실됐다(src="" → 라이브러리 parseDOM drop).
 */
function resolveCidSource(img: HTMLImageElement): string | null {
  const src = img.getAttribute('src')
  if (src != null && src.toLowerCase().indexOf('cid:') === 0) return src
  for (const attr of ['owa-cid', 'data-cid']) {
    const value = img.getAttribute(attr)
    if (value != null && value.toLowerCase().indexOf('cid:') === 0) return value
  }
  return null
}

/**
 * `convertCidImagesToDataUrl` 의 Document 반환 variant — 직렬화 직전의(cid 변환 완료된)
 * Document 를 그대로 반환한다. 대형 본문에서 "직렬화 → getSafeHTML 내부 재파싱" 왕복을
 * 제거하기 위한 용도(reply 인용 bake): getSafeHTML 은 Document 입력을 받으면 내부 DOMParser
 * 재파싱을 생략한다(OFM-221). 반환 Document 는 getSafeHTML 전달 시 adopt 로 소진(mutate)되므로
 * 호출부는 전달 후 재사용하면 안 된다. 문자열이 필요한 소비처는 기존
 * `convertCidImagesToDataUrl` 을 그대로 사용한다.
 */
export async function convertCidImagesToDocument(
  bodyHTML: string | Document,
  attachmentLookup: (cid: string) => CidImageAttachment | undefined | null,
  registerMapping: (cidSrc: string, dataUrl: string) => void,
): Promise<Document> {
  // 이미 파싱된 Document 를 받으면 재파싱을 생략한다(대형 draft 의 동일 문자열 중복 파싱 제거 —
  // EmailForm initEditor 의 reopen seeding 파싱을 공유). 반환 Document 와 동일하게 본 함수가
  // mutate(img src 교체)하고 이후 setDataFromDOM/getSafeElement 가 소진하므로, 호출부는 전달 후
  // 해당 Document 를 재사용하면 안 된다.
  const doc =
    typeof bodyHTML === 'string' ? new DOMParser().parseFromString(bodyHTML, 'text/html') : bodyHTML
  const imgList = doc.querySelectorAll('img')

  const promises: Array<Promise<void>> = []
  imgList.forEach((img) => {
    const cidSrc = resolveCidSource(img)
    if (cidSrc == null) return
    const cid = cidSrc.substring(4)
    const attachment = attachmentLookup(cid)
    if (attachment == null) {
      img.removeAttribute('src')
      img.removeAttribute('owa-cid')
      img.setAttribute('alt', 'missing inline image')
      return
    }
    promises.push(
      (async () => {
        const downloadObj = await attachment.download()
        const blob = downloadObj?.blob
        if (blob == null) {
          img.removeAttribute('src')
          img.removeAttribute('owa-cid')
          img.setAttribute('alt', 'missing inline image')
          return
        }
        const dataUrl = await blobToDataUrl(blob)
        img.setAttribute('src', dataUrl)
        // EmailView 가 백업한 owa-cid 는 라이브러리 schema 미인식 attr 이므로 제거(역치환은 urlMap 담당).
        img.removeAttribute('owa-cid')
        registerMapping(cidSrc, dataUrl)
      })(),
    )
  })

  await Promise.all(promises)

  return doc
}

export async function convertCidImagesToDataUrl(
  bodyHTML: string,
  attachmentLookup: (cid: string) => CidImageAttachment | undefined | null,
  registerMapping: (cidSrc: string, dataUrl: string) => void,
): Promise<string> {
  const doc = await convertCidImagesToDocument(bodyHTML, attachmentLookup, registerMapping)

  const lowerText = bodyHTML.toLowerCase()
  if (lowerText.indexOf('<!doctype') > -1 || lowerText.indexOf('<!DOCTYPE') > -1) {
    return new XMLSerializer().serializeToString(doc)
  }

  return doc.body.innerHTML
}
